import {
  IconAlertTriangle,
  IconBell,
  IconBook,
  IconBuilding,
  IconCalculator,
  IconCalendar,
  IconCalendarCheck,
  IconCamera,
  IconChartBar,
  IconClipboardCheck,
  IconClipboardList,
  //  IconHomeSignal,
  IconClockExclamation,
  IconClockHour10,
  IconContract,
  IconCurrencyDollar,
  //  IconFileDollar,
  IconEyeCheck,
  IconFlame,
  IconGauge,
  IconListNumbers,
  IconMail,
  IconMap2,
  IconMapPin,
  IconMapRoute,
  IconMessageChatbot,
  IconMessageStar,
  IconPackage,
  IconPhoneCall,
  IconReceiptDollar,
  IconRobot,
  // IconMapCog,
  // IconPlugConnected,
  IconSettings,
  // IconPhoneCheck,
  IconTrolley,
  IconTrophy,
  //  IconSpeakerphone,
  IconTruck,
  IconUserExclamation,
  IconUsers,
  IconUserSearch,
} from '@tabler/icons-react'

import { userCanPerformAction } from '@/auth/authorizations'
import { RoleEnum, RoleGroup } from '@/models'
import { EmmaPath } from '@/router/paths'
import { VITE_ENVIRONMENT } from '@/utils/constants'

const isDashUrl = window.location.hostname === 'dash.einsteinmoving.com'
const isProd = VITE_ENVIRONMENT === 'prod'

export interface IMenuItem {
  label: string
  path: string
  icon: React.ElementType
  authorized: boolean
  disabled: boolean
  badge?: number | undefined
  external?: boolean
}

export interface IMenuItems {
  label: string
  disabled: boolean
  subMenu: {
    items: IMenuItem[]
  }
}

export const menuItems = (
  userRoles: RoleGroup,
  newLeadsQueueSize: number | undefined,
  autoBookingsQueueSize: number | undefined,
  salesReviewQueueSize: number | undefined,
  pendingApprovalsCount: number | undefined,
): IMenuItems[] => [
  {
    label: 'Customers',
    disabled: isDashUrl,
    subMenu: {
      items: [
        {
          label: 'All Customers',
          path: EmmaPath.CUSTOMER_LISTING,
          icon: IconUserSearch,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.CUSTOMER_LISTING),
          disabled: isDashUrl,
        },
        {
          label: 'New Leads',
          path: EmmaPath.CUSTOMER_NEW_LEADS,
          icon: IconFlame,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.CUSTOMER_NEW_LEADS),
          disabled: isDashUrl,
          badge: newLeadsQueueSize,
        },
        {
          label: 'New Leads Triage',
          path: EmmaPath.CUSTOMER_NEW_LEADS_TRIAGE,
          icon: IconListNumbers,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.CUSTOMER_NEW_LEADS_TRIAGE),
          disabled: isDashUrl,
          badge: newLeadsQueueSize,
        },
        {
          label: 'New Leads Triage v2',
          path: EmmaPath.CUSTOMER_NEW_LEADS_TRIAGE_V2,
          icon: IconListNumbers,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.CUSTOMER_NEW_LEADS_TRIAGE_V2),
          disabled: isDashUrl,
          badge: newLeadsQueueSize,
        },
        {
          label: 'New Leads Triage v4',
          path: EmmaPath.CUSTOMER_NEW_LEADS_TRIAGE_V4,
          icon: IconListNumbers,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.CUSTOMER_NEW_LEADS_TRIAGE_V4),
          disabled: isDashUrl,
          badge: newLeadsQueueSize,
        },
        {
          label: 'Auto Bookings',
          path: EmmaPath.CUSTOMER_AUTO_BOOKINGS,
          icon: IconCalendarCheck,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.CUSTOMER_AUTO_BOOKINGS),
          disabled: isDashUrl,
          badge: autoBookingsQueueSize,
        },
        {
          label: 'Sales Review',
          path: EmmaPath.SALES_MANAGER_REVIEW,
          icon: IconEyeCheck,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.SALES_MANAGER_REVIEW),
          disabled: isDashUrl,
          badge: salesReviewQueueSize,
        },
        {
          label: 'Emma Leads',
          path: EmmaPath.EMMA_LEADS,
          icon: IconRobot,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.EMMA_LEADS),
          disabled: isDashUrl,
        },
        // {
        //   label: 'Enhanced Sales',
        //   path: EmmaPath.CUSTOMER_ENHANCED_SALES,
        //   icon: IconCalendarQuestion,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.CUSTOMER_ENHANCED_SALES),
        //   disabled: isDashUrl,
        // },
      ],
    },
  },
  {
    label: 'Moves',
    disabled: isDashUrl,
    subMenu: {
      items: [
        {
          label: 'Move Manager',
          path: EmmaPath.MOVE_MANAGER,
          icon: IconTrolley,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.MOVE_MANAGER),
          disabled: isDashUrl,
        },
        {
          label: 'Mover Moves',
          path: EmmaPath.MOVER_MOVES,
          icon: IconPackage,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.MOVER_MOVES),
          disabled: isDashUrl,
        },
      ],
    },
  },
  {
    label: 'Tools',
    disabled: false,
    subMenu: {
      items: [
        {
          label: 'Quoting Guide',
          path: 'https://einsteinadmin.github.io/quick-quote/',
          icon: IconCalculator,
          authorized: userRoles.some((role) =>
            [RoleEnum.admin, RoleEnum.manager, RoleEnum.sales].includes(role as RoleEnum),
          ),
          disabled: false,
          external: true,
        },
        {
          label: 'Estimation Simulator',
          path: EmmaPath.ADMIN_ESTIMATION_SIMULATOR,
          icon: IconGauge,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_ESTIMATION_SIMULATOR),
          disabled: false,
        },
        {
          label: 'Reassignment Form',
          path: EmmaPath.ADMIN_REASSIGNMENT_FORM,
          icon: IconUserExclamation,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_REASSIGNMENT_FORM),
          disabled: false,
        },
      ],
    },
  },
  {
    label: 'Dashboards & Reports',
    disabled: false,
    subMenu: {
      items: [
        {
          label: 'Overtime Report',
          path: EmmaPath.REPORT_OVER_TIME,
          icon: IconClockExclamation,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_OVER_TIME),
          disabled: false,
        },
        // {
        //   label: 'Call Answer Rate',
        //   path: EmmaPath.ADMIN_DIALPAD_STATS_LISTING,
        //   icon: IconPhoneCheck,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_DIALPAD_STATS_LISTING),
        //   disabled: isDashUrl,
        // },
        {
          label: 'Sales Dashboard',
          path: EmmaPath.REPORT_SALES,
          icon: IconChartBar,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_SALES),
          disabled: false,
        },
        // {
        //   label: 'Revenue Dashboard',
        //   path: EmmaPath.REPORT_REVENUE,
        //   icon: IconFileDollar,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_REVENUE),
        //   disabled: false,
        // },
        // {
        //   label: 'KPI Dashboard',
        //   path: EmmaPath.REPORT_KPIS,
        //   icon: IconGauge,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_KPIS),
        //   disabled: false,
        // },
        {
          label: 'Branch KPIs',
          path: EmmaPath.REPORT_BRANCH_KPIS,
          icon: IconGauge,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_BRANCH_KPIS),
          disabled: false,
        },
        // {
        //   label: 'Mover Games',
        //   path: EmmaPath.REPORT_MOVER_GAMES,
        //   icon: IconTrophy,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_MOVER_GAMES),
        //   disabled: false,
        // },
        {
          label: 'Einstein Games',
          path: EmmaPath.REPORT_EINSTEIN_GAMES,
          icon: IconTrophy,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_EINSTEIN_GAMES),
          disabled: isProd,
        },
        {
          label: 'Snapshot',
          path: EmmaPath.REPORT_SNAPSHOT,
          icon: IconCamera,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_SNAPSHOT),
          disabled: isProd,
        },
        // {
        //   label: 'Branch Stats',
        //   path: EmmaPath.REPORT_GENERAL,
        //   icon: IconHomeSignal,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_GENERAL),
        //   disabled: false,
        // },
        {
          label: 'Payroll Export',
          path: EmmaPath.ADMIN_MOVE_REPORTS,
          icon: IconReceiptDollar,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_MOVE_REPORTS),
          disabled: isDashUrl,
        },
        // {
        //   label: 'Trip Tracker',
        //   path: EmmaPath.ADMIN_TRIP_TRACKER_LISTING,
        //   icon: IconChartBar,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_TRIP_TRACKER_LISTING),
        //   disabled: isDashUrl,
        // },
        // {
        //   label: 'Rippling Work Hours',
        //   path: EmmaPath.ADMIN_RIPPLING_WORK_HOURS_LISTING,
        //   icon: IconClockHour10,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_RIPPLING_WORK_HOURS_LISTING),
        //   disabled: isDashUrl,
        // },
        // {
        //   label: 'NPS',
        //   path: EmmaPath.ADMIN_NPS_LISTING,
        //   icon: IconGraph,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_NPS_LISTING),
        //   disabled: isDashUrl,
        // },
        // {
        //   label: 'Reviews',
        //   path: EmmaPath.REPORT_REVIEWS,
        //   icon: IconStar,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_REVIEWS),
        //   disabled: isDashUrl,
        // },
        // {
        //   label: 'Damages',
        //   path: EmmaPath.REPORT_DAMAGES,
        //   icon: IconCarCrash,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_DAMAGES),
        //   disabled: isDashUrl,
        // },
        // {
        //   label: 'KPIs',
        //   path: EmmaPath.REPORT_KPIS,
        //   icon: IconGauge,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_KPIS),
        //   disabled: isDashUrl,
        // },
        // {
        //   label: 'General',
        //   path: EmmaPath.REPORT_GENERAL,
        //   icon: IconChartBar,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_GENERAL),
        //   disabled: isDashUrl,
        // },
        // {
        //   label: 'TUR',
        //   path: EmmaPath.REPORT_TUR,
        //   icon: IconChartLine,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_TUR),
        //   disabled: false,
        // },
        // {
        //   label: 'Sales Pipeline',
        //   path: EmmaPath.REPORT_SALES_PIPELINE,
        //   icon: IconChartSankey,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_SALES_PIPELINE),
        //   disabled: false,
        // },
        // {
        //   label: 'Revenue',
        //   path: EmmaPath.REPORT_REVENUE,
        //   icon: IconCurrencyDollar,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_REVENUE),
        //   disabled: false,
        // },
        // {
        //   label: 'Billable Efficiency',
        //   path: EmmaPath.REPORT_BILLABLE_EFFICIENCY,
        //   icon: IconFileDollar,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_BILLABLE_EFFICIENCY),
        //   disabled: isDashUrl,
        // },
        {
          label: 'Contract Efficiency',
          path: EmmaPath.REPORT_CONTRACT_EFFICIENCY,
          icon: IconContract,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_CONTRACT_EFFICIENCY),
          disabled: isDashUrl,
        },
        {
          label: 'Marketing Dashboard',
          path: EmmaPath.REPORT_MARKETING_DASHBOARD,
          icon: IconGauge,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_MARKETING_DASHBOARD),
          disabled: false,
        },
        // {
        //   label: 'NPS',
        //   path: EmmaPath.REPORT_NPS,
        //   icon: IconGraph,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_NPS),
        //   disabled: isDashUrl,
        // },
        // {
        //   label: 'Over Time',
        //   path: EmmaPath.REPORT_OVER_TIME,
        //   icon: IconAlarm,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_OVER_TIME),
        //   disabled: false,
        // },
      ],
    },
  },
  {
    label: 'Administration',
    disabled: isDashUrl,
    subMenu: {
      items: [
        {
          label: 'Branches',
          path: EmmaPath.ADMIN_BRANCH_LISTING,
          icon: IconBuilding,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_BRANCH_LISTING),
          disabled: isDashUrl,
        },
        {
          label: 'Trucks',
          path: EmmaPath.ADMIN_TRUCK_LISTING,
          icon: IconTruck,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_TRUCK_LISTING),
          disabled: isDashUrl,
        },
        {
          label: 'Employees',
          path: EmmaPath.ADMIN_EMPLOYEE_LISTING,
          icon: IconUsers,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_EMPLOYEE_LISTING),
          disabled: isDashUrl,
        },
        // {
        //   label: 'Service Zones',
        //   path: EmmaPath.ADMIN_EMPLOYEE_LISTING,
        //   icon: IconMapCog,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_EMPLOYEE_LISTING),
        //   disabled: isDashUrl,
        // },
        {
          label: 'Problem Locations',
          path: EmmaPath.ADMIN_PROBLEMS_LOCATIONS,
          icon: IconAlertTriangle,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_PROBLEMS_LOCATIONS),
          disabled: isDashUrl,
        },
        // {
        //   label: 'Marketing Sources',
        //   path: EmmaPath.ADMIN_PROBLEMS_LOCATIONS,
        //   icon: IconSpeakerphone,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_PROBLEMS_LOCATIONS),
        //   disabled: isDashUrl,
        // },
        // {
        //   label: 'Data Connectors',
        //   path: EmmaPath.ADMIN_PROBLEMS_LOCATIONS,
        //   icon: IconPlugConnected,
        //   authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_PROBLEMS_LOCATIONS),
        //   disabled: isDashUrl,
        // },
        {
          label: 'Email Templates',
          path: EmmaPath.ADMIN_EMAIL_TEMPLATES,
          icon: IconMail,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_EMAIL_TEMPLATES),
          disabled: isDashUrl,
        },
        {
          label: 'Leadferno Conversion',
          path: EmmaPath.ADMIN_LEADFERNO_CONVERSION,
          icon: IconMessageChatbot,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_LEADFERNO_CONVERSION),
          disabled: isDashUrl,
        },
        {
          label: 'Performance Log',
          path: EmmaPath.ADMIN_PERFORMANCE_LOG,
          icon: IconPhoneCall,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_PERFORMANCE_LOG),
          disabled: isDashUrl,
        },
        {
          label: 'Settings',
          path: EmmaPath.REPORT_SETTINGS,
          icon: IconSettings,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.REPORT_SETTINGS),
          disabled: false,
        },
      ],
    },
  },
  {
    label: 'Super Admin',
    disabled: isDashUrl,
    subMenu: {
      items: [
        {
          label: 'Calendar Availabilities',
          path: EmmaPath.ADMIN_CALENDAR_AVAILABILITIES,
          icon: IconCalendar,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_CALENDAR_AVAILABILITIES),
          disabled: isDashUrl,
        },
        {
          label: 'Notification Rules',
          path: EmmaPath.ADMIN_NOTIFICATION_RULES,
          icon: IconBell,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_NOTIFICATION_RULES),
          disabled: isDashUrl,
        },
        {
          label: 'Tracking Incorrect Moves',
          path: EmmaPath.ADMIN_TRACKING_INCORRECT_MOVES,
          icon: IconAlertTriangle,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_TRACKING_INCORRECT_MOVES),
          disabled: isDashUrl,
        },
        {
          label: 'Pricing Rules',
          path: EmmaPath.ADMIN_PRICING_RULES,
          icon: IconCurrencyDollar,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_PRICING_RULES),
          disabled: isDashUrl,
        },
        {
          label: 'Zip Zones',
          path: EmmaPath.ADMIN_ZIP_ZONES,
          icon: IconMapPin,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_ZIP_ZONES),
          disabled: isDashUrl,
        },
        {
          label: 'Service Zones',
          path: EmmaPath.ADMIN_SERVICE_ZONES,
          icon: IconMap2,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_SERVICE_ZONES),
          disabled: isDashUrl,
        },
        {
          label: 'Cron Jobs',
          path: EmmaPath.ADMIN_CRON_JOBS,
          icon: IconClockHour10,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_CRON_JOBS),
          disabled: isDashUrl,
        },
        {
          label: 'Booking Simulator',
          path: EmmaPath.ADMIN_BOOKING_SIMULATOR,
          icon: IconClipboardList,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_BOOKING_SIMULATOR),
          disabled: isDashUrl,
        },
        {
          label: 'Documentation',
          path: '/emma/admin/docs/documentation',
          icon: IconBook,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_DOCS),
          disabled: isDashUrl,
        },
        {
          label: 'User Handbook',
          path: '/emma/admin/docs/handbook',
          icon: IconBook,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_DOCS),
          disabled: isDashUrl,
        },
        {
          label: 'Zone Map',
          path: EmmaPath.ADMIN_MAP,
          icon: IconMapPin,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_MAP),
          disabled: isDashUrl,
        },
        {
          label: 'Revenue Map',
          path: EmmaPath.ADMIN_MOVES_MAP,
          icon: IconMap2,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_MOVES_MAP),
          disabled: isDashUrl,
        },
        {
          label: 'Relocation Map',
          path: EmmaPath.ADMIN_RELOCATION_TREND_MAP,
          icon: IconMapRoute,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_RELOCATION_TREND_MAP),
          disabled: isDashUrl,
        },
        {
          label: 'Call Out Approvals',
          path: EmmaPath.ADMIN_ATTENDANCE_APPROVALS,
          icon: IconClipboardCheck,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ADMIN_ATTENDANCE_APPROVALS),
          disabled: isDashUrl,
          badge: pendingApprovalsCount ?? undefined,
        },
        {
          label: 'Quick Praise/Redirects',
          path: EmmaPath.ECP_QUICK_PRAISE_REDIRECTS,
          icon: IconMessageStar,
          authorized: userCanPerformAction(false, userRoles, EmmaPath.ECP_QUICK_PRAISE_REDIRECTS),
          disabled: false,
        },
      ],
    },
  },
]
